import { useState, useEffect } from 'react';
import { Plus, X, Save, ArrowRight, DollarSign, Clock, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { JobPostingFormData, BroUser, JobCategory } from '../types';
import CategorySelector from './CategorySelector';
import BudgetCalculator from './BudgetCalculator';
import { useJobPosting } from '../hooks/useJobPosting';
import { useToast } from '@/components/ui/use-toast';
import { useAuthenticatedApi } from '@/hooks/useAuthenticatedApi';

interface JobPostingFormProps {
  user: BroUser;
  onBack: () => void;
  onJobCreated?: (job: any) => void;
}

const JobPostingForm = ({ user, onBack, onJobCreated }: JobPostingFormProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const { createJob, loading: isCreating, error: createError, estimateJobCost } = useJobPosting(user.id);
  const { invokeFunction } = useAuthenticatedApi();
  const { toast } = useToast();
  const [categories, setCategories] = useState<JobCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [formData, setFormData] = useState<JobPostingFormData>({
    title: '',
    description: '',
    category_id: '',
    budget_currency: 'BRO_SCORE',
    payment_type: 'fixed',
    required_skills: [],
    experience_level: 'intermediate',
    min_bro_score: 0,
    requires_verification: false,
    priority: 'normal',
    featured: false,
    remote_allowed: true,
    auto_assign: false,
    tags: []
  });

  const [newSkill, setNewSkill] = useState('');
  const [newTag, setNewTag] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch real categories from database
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        console.log('🎯 [CATEGORIES] Fetching categories from database...');
        
        const response = await invokeFunction('job-board-api', {
          action: 'get_job_categories'
        });
        
        console.log('🎯 [CATEGORIES] Categories response:', response);
        
        if (response?.success && response?.data?.categories) {
          setCategories(response.data.categories);
          console.log('✅ [CATEGORIES] Loaded categories:', response.data.categories);
        } else {
          console.error('❌ [CATEGORIES] Failed to fetch categories:', response);
          toast({
            title: "Warning",
            description: "Could not load job categories. Please refresh the page.",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error('❌ [CATEGORIES] Error fetching categories:', error);
        toast({
          title: "Error",
          description: "Failed to load job categories. Please refresh the page.",
          variant: "destructive",
        });
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, [invokeFunction, toast]);

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 1) {
      if (!formData.title.trim()) {
        newErrors.title = 'Job title is required';
      } else if (formData.title.length < 10) {
        newErrors.title = 'Job title must be at least 10 characters';
      }

      if (!formData.description.trim()) {
        newErrors.description = 'Job description is required';
      } else if (formData.description.length < 100) {
        newErrors.description = 'Job description must be at least 100 characters';
      }

      if (!formData.category_id) {
        newErrors.category_id = 'Please select a category';
      }
    }

    if (step === 2) {
      if (formData.required_skills.length === 0) {
        newErrors.required_skills = 'At least one skill is required';
      }

      if (formData.budget_min && formData.budget_max && formData.budget_min > formData.budget_max) {
        newErrors.budget_max = 'Maximum budget must be greater than minimum budget';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const addSkill = () => {
    if (newSkill.trim() && !formData.required_skills.includes(newSkill.trim())) {
      setFormData(prev => ({
        ...prev,
        required_skills: [...prev.required_skills, newSkill.trim()]
      }));
      setNewSkill('');
    }
  };

  const removeSkill = (index: number) => {
    setFormData(prev => ({
      ...prev,
      required_skills: prev.required_skills.filter((_, i) => i !== index)
    }));
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim().toLowerCase())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim().toLowerCase()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (index: number) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setIsSubmitting(true);
    
    try {
      console.log('🎯 [JOB_FORM] Starting job submission...', formData);
      const result = await createJob(formData);
      console.log('🎯 [JOB_FORM] Job creation result:', result);
      
      if (result.success) {
        toast({
          title: "Job Created Successfully!",
          description: `Your job "${formData.title}" has been posted.`,
        });
        if (result.job) {
          onJobCreated?.(result.job);
        }
        onBack();
      } else {
        console.error('❌ [JOB_FORM] Job creation failed:', result.error);
        toast({
          title: "Failed to Create Job",
          description: result.error || "Please check your form and try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('💥 [JOB_FORM] Unexpected error:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStepTitle = (step: number) => {
    switch (step) {
      case 1: return 'Job Details';
      case 2: return 'Requirements & Budget';
      case 3: return 'Settings & Review';
      default: return 'Create Job';
    }
  };

  return (
    <div className="space-y-6">
      {/* Progress Steps */}
      <div className="flex items-center justify-between">
        {[1, 2, 3].map((step) => (
          <div key={step} className="flex items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                step <= currentStep
                  ? 'bg-tan text-navy border-tan'
                  : 'border-tan-dark text-tan-dark'
              }`}
            >
              {step}
            </div>
            {step < 3 && (
              <div
                className={`w-16 h-0.5 mx-2 ${
                  step < currentStep ? 'bg-tan' : 'bg-tan-dark'
                }`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Step Title */}
      <div className="text-center">
        <h2 className="text-xl font-bold text-tan">{getStepTitle(currentStep)}</h2>
        <p className="text-tan-dark text-sm">Step {currentStep} of 3</p>
      </div>

      {/* Step 1: Basic Job Information */}
      {currentStep === 1 && (
        <div className="space-y-4">
          {/* Job Title */}
          <div>
            <Label htmlFor="title" className="text-tan font-medium">
              Job Title *
            </Label>
            <Input
              id="title"
              placeholder="e.g., Smart Contract Developer for DeFi Protocol"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className={`mt-1 bg-navy border-tan-dark text-tan placeholder-tan-dark ${
                errors.title ? 'border-red-500' : ''
              }`}
            />
            {errors.title && (
              <p className="text-red-400 text-xs mt-1">{errors.title}</p>
            )}
            <p className="text-tan-dark text-xs mt-1">
              {formData.title.length}/100 characters (minimum 10)
            </p>
          </div>

          {/* Category */}
          <div>
            <Label className="text-tan font-medium">
              Category *
            </Label>
            {loadingCategories ? (
              <div className="mt-1 p-3 bg-navy border border-tan-dark rounded-lg">
                <p className="text-tan-dark text-sm">Loading categories...</p>
              </div>
            ) : (
              <CategorySelector
                categories={categories}
                selectedCategory={formData.category_id}
                onSelect={(categoryId) => setFormData(prev => ({ ...prev, category_id: categoryId }))}
                error={errors.category_id}
              />
            )}
          </div>

          {/* Job Description */}
          <div>
            <Label htmlFor="description" className="text-tan font-medium">
              Job Description *
            </Label>
            <Textarea
              id="description"
              placeholder="Provide a detailed description of the job, including specific requirements, deliverables, and what makes this opportunity exciting..."
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={8}
              className={`mt-1 bg-navy border-tan-dark text-tan placeholder-tan-dark ${
                errors.description ? 'border-red-500' : ''
              }`}
            />
            {errors.description && (
              <p className="text-red-400 text-xs mt-1">{errors.description}</p>
            )}
            <p className="text-tan-dark text-xs mt-1">
              {formData.description.length}/2000 characters (minimum 100)
            </p>
          </div>
        </div>
      )}

      {/* Step 2: Requirements and Budget */}
      {currentStep === 2 && (
        <div className="space-y-4">
          {/* Required Skills */}
          <div>
            <Label className="text-tan font-medium">
              Required Skills *
            </Label>
            <div className="mt-2 space-y-2">
              <div className="flex gap-2">
                <Input
                  placeholder="e.g., React, Solidity, TypeScript"
                  value={newSkill}
                  onChange={(e) => setNewSkill(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                  className="bg-navy border-tan-dark text-tan placeholder-tan-dark"
                />
                <Button
                  type="button"
                  onClick={addSkill}
                  variant="outline"
                  className="border-tan-dark text-tan hover:bg-tan hover:text-navy"
                >
                  <Plus size={16} />
                </Button>
              </div>

              <div className="flex flex-wrap gap-2">
                {formData.required_skills.map((skill, index) => (
                  <div key={index} className="flex items-center gap-1 bg-navy rounded px-2 py-1 border border-tan-dark">
                    <span className="text-tan text-sm">{skill}</span>
                    <Button
                      type="button"
                      onClick={() => removeSkill(index)}
                      variant="ghost"
                      size="sm"
                      className="p-0 h-auto text-red-400 hover:text-red-300"
                    >
                      <X size={12} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            {errors.required_skills && (
              <p className="text-red-400 text-xs mt-1">{errors.required_skills}</p>
            )}
          </div>

          {/* Experience Level */}
          <div>
            <Label className="text-tan font-medium">
              Experience Level
            </Label>
            <Select 
              value={formData.experience_level} 
              onValueChange={(value) => setFormData(prev => ({ 
                ...prev, 
                experience_level: value as 'entry' | 'intermediate' | 'expert'
              }))}
            >
              <SelectTrigger className="mt-1 bg-navy border-tan-dark text-tan">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-navy border-tan-dark">
                <SelectItem value="entry">Entry Level</SelectItem>
                <SelectItem value="intermediate">Intermediate</SelectItem>
                <SelectItem value="expert">Expert</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Budget Calculator */}
          <BudgetCalculator
            formData={formData}
            onChange={(budgetData) => setFormData(prev => ({ ...prev, ...budgetData }))}
            errors={errors}
          />

          {/* Timeline */}
          <div>
            <Label htmlFor="estimated_duration" className="text-tan font-medium">
              Estimated Duration
            </Label>
            <div className="relative mt-1">
              <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-tan-dark" size={16} />
              <Input
                id="estimated_duration"
                placeholder="e.g., 2 weeks, 1 month, 3 days"
                value={formData.estimated_duration || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, estimated_duration: e.target.value }))}
                className="pl-10 bg-navy border-tan-dark text-tan placeholder-tan-dark"
              />
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Settings and Review */}
      {currentStep === 3 && (
        <div className="space-y-4">
          {/* Job Requirements */}
          <div className="bg-navy-light rounded-lg p-4 border border-tan-dark">
            <h3 className="text-tan font-bold mb-3">Job Requirements</h3>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-tan font-medium">Minimum BroScore</Label>
                  <p className="text-tan-dark text-xs">Require applicants to have minimum BroScore</p>
                </div>
                <Input
                  type="number"
                  value={formData.min_bro_score}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    min_bro_score: Number(e.target.value) || 0 
                  }))}
                  className="w-24 bg-navy border-tan-dark text-tan"
                  min="0"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-tan font-medium">Telegram Verification</Label>
                  <p className="text-tan-dark text-xs">Require applicants to have verified Telegram</p>
                </div>
                <Switch
                  checked={formData.requires_verification}
                  onCheckedChange={(checked) => setFormData(prev => ({ 
                    ...prev, 
                    requires_verification: checked 
                  }))}
                />
              </div>
            </div>
          </div>

          {/* Job Settings */}
          <div className="bg-navy-light rounded-lg p-4 border border-tan-dark">
            <h3 className="text-tan font-bold mb-3">Job Settings</h3>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-tan font-medium">Remote Work</Label>
                  <p className="text-tan-dark text-xs">Allow remote work for this position</p>
                </div>
                <Switch
                  checked={formData.remote_allowed}
                  onCheckedChange={(checked) => setFormData(prev => ({ 
                    ...prev, 
                    remote_allowed: checked 
                  }))}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-tan font-medium">Featured Job</Label>
                  <p className="text-tan-dark text-xs">Highlight this job (costs extra BroScore)</p>
                </div>
                <Switch
                  checked={formData.featured}
                  onCheckedChange={(checked) => setFormData(prev => ({ 
                    ...prev, 
                    featured: checked 
                  }))}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-tan font-medium">Auto-assign</Label>
                  <p className="text-tan-dark text-xs">Automatically assign to first qualified applicant</p>
                </div>
                <Switch
                  checked={formData.auto_assign}
                  onCheckedChange={(checked) => setFormData(prev => ({ 
                    ...prev, 
                    auto_assign: checked 
                  }))}
                />
              </div>
            </div>
          </div>

          {/* Location */}
          <div>
            <Label htmlFor="location" className="text-tan font-medium">
              Location (Optional)
            </Label>
            <div className="relative mt-1">
              <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-tan-dark" size={16} />
              <Input
                id="location"
                placeholder="e.g., Remote, New York, Global"
                value={formData.location || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                className="pl-10 bg-navy border-tan-dark text-tan placeholder-tan-dark"
              />
            </div>
          </div>

          {/* Tags */}
          <div>
            <Label className="text-tan font-medium">
              Tags (Optional)
            </Label>
            <div className="mt-2 space-y-2">
              <div className="flex gap-2">
                <Input
                  placeholder="e.g., urgent, startup, fintech"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  className="bg-navy border-tan-dark text-tan placeholder-tan-dark"
                />
                <Button
                  type="button"
                  onClick={addTag}
                  variant="outline"
                  className="border-tan-dark text-tan hover:bg-tan hover:text-navy"
                >
                  <Plus size={16} />
                </Button>
              </div>

              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag, index) => (
                  <div key={index} className="flex items-center gap-1 bg-navy rounded px-2 py-1 border border-tan-dark">
                    <span className="text-tan text-sm">#{tag}</span>
                    <Button
                      type="button"
                      onClick={() => removeTag(index)}
                      variant="ghost"
                      size="sm"
                      className="p-0 h-auto text-red-400 hover:text-red-300"
                    >
                      <X size={12} />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Cost Estimate */}
          <Alert>
            <DollarSign className="h-4 w-4" />
            <AlertDescription>
              <strong>Posting Cost:</strong> {estimateJobCost(formData)} BroScore
              {formData.featured && ' (includes featured placement)'}
            </AlertDescription>
          </Alert>

          {/* Error Display */}
          {createError && (
            <Alert className="border-red-500 bg-red-500/10">
              <AlertDescription className="text-red-400">
                {createError}
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-4">
        <Button
          onClick={currentStep === 1 ? onBack : handlePrevStep}
          variant="outline"
          className="border-tan-dark text-tan hover:bg-tan hover:text-navy"
        >
          {currentStep === 1 ? 'Cancel' : 'Previous'}
        </Button>

        {currentStep < 3 ? (
          <Button
            onClick={handleNextStep}
            className="bg-tan text-navy hover:bg-tan-dark"
          >
            Next Step
            <ArrowRight size={16} className="ml-2" />
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || isCreating}
            className="bg-tan text-navy hover:bg-tan-dark"
          >
            {(isSubmitting || isCreating) ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-navy mr-2"></div>
                Creating Job...
              </>
            ) : (
              <>
                <Save size={16} className="mr-2" />
                Create Job
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default JobPostingForm;